/**
 * Settings mappers for converting between BE step settings and FE formData
 */

import { StepOutcome } from "components/CC/types/enums";
import { FilterType } from "types/enums";

import { mapFlowToMeaning, mapMeaningToFlow } from "./typeMappers";

import StepData = Paldock.LaravelConnectionCreator.Data.StepData;
import ConnectionData = Paldock.LaravelConnectionCreator.Data.ConnectionData;

const BREAKER_MAX_REPETITION_PATTERN = /^(?:[1-9]\d*|(?=.*\{[^{}]+\})(?:\d+|\{[^{}]+\})+)$/;

// ============================================================================
// BE -> FE Mappers (Step settings to formData)
// ============================================================================

/**
 * Map BE step to FE formData based on step type
 */
export function mapStepSettingsToFormData(step: StepData): Record<string, unknown> {
    const baseData = {
        active: step.is_enabled,
        title: step.title || "",
        subtitle: step.subtitle || "",
    };

    // BE types say Array<any> but actual API sends an object
    const settings = step.settings as unknown as Record<string, unknown> | undefined;

    switch (step.type) {
        case "integration":
            return {
                ...baseData,
                flow: mapMeaningToFlow((settings?.meaning as string) || "send"),
                beType: step.type,
            };

        case "incoming_postback":
            return {
                ...baseData,
                flow: "webhook",
                beType: step.type,
            };

        case "outgoing_postback":
            return {
                ...baseData,
                flow: "instant",
                beType: step.type,
            };

        case "validation":
        case "structure":
            return {
                ...baseData,
                flow: "instant",
                beType: step.type,
            };

        case "http":
            return {
                ...baseData,
                method: (settings?.method as string) || "GET",
                endpoint: (settings?.uri as string) || "",
                timeout: parseInt((settings?.timeout as string) || "30", 10),
                format: (settings?.format as string) || null,
                parser: (settings?.parser as string) || "json",
                notsend: (settings?.notsend as string) || null,
                convert_data: (settings?.convert_data as string) || null,
                headers: mapBeHeadersToFe(settings?.headers),
                query: mapBeQueryToFe(settings?.query),
                body: mapBeBodyToFe(settings?.body),
                signature: mapBeSignatureToFe(settings?.signature),
                insecure: (settings?.insecure as boolean) ?? null,
            };

        case "delay": {
            const delaySeconds = (settings?.delay_seconds as number) || 0;
            return {
                ...baseData,
                type: "delay" as const,
                delayValue: delaySeconds,
                delayUnit: "seconds" as const,
                untilDate: null,
            };
        }

        case "set":
            return {
                ...baseData,
                fields: mapBeSetValuesToFe(settings?.values),
            };

        case "modify": {
            const modifyData = mapBeModifyValuesToFe(settings?.values);
            return {
                ...baseData,
                // If no data from BE, provide default empty entry
                data:
                    modifyData.length > 0
                        ? modifyData
                        : [
                              {
                                  fieldName: "",
                                  fields: [
                                      {
                                          source: "",
                                          operator: "",
                                          value: "",
                                          value2: "",
                                          modification: "",
                                          pattern: "",
                                          output: "",
                                      },
                                  ],
                              },
                          ],
            };
        }

        case "repeater":
            return {
                ...baseData,
                maxRepetition: (settings?.repeats as number) || 1,
            };

        case "breaker": {
            const repsRaw = String(settings?.repeats ?? "").trim();
            const maxRepetition = BREAKER_MAX_REPETITION_PATTERN.test(repsRaw) ? repsRaw : "1";
            return { ...baseData, maxRepetition };
        }

        case "mirror":
            return {
                ...baseData,
                mirrorElement: (settings?.mirror_step_id as string) || "",
            };

        case "sub_scenario":
            return {
                ...baseData,
                scenario: (settings?.scenario_id as string) || "",
            };

        case "webhook":
            return {
                ...baseData,
                wait: (settings?.wait_for_webhook as boolean) ?? false,
                timeout: parseInt(String(settings?.timeout ?? "40"), 10),
                token: (settings?.token as string) || "",
            };

        case "success":
            return {
                ...baseData,
                success: (settings?.success as boolean) === false ? StepOutcome.Failed : StepOutcome.Success,
            };

        case "condition":
            return baseData;

        default:
            return baseData;
    }
}

/**
 * Map BE connection settings to FE edge data
 *
 * BE FilterSettingsRequest: { path: string, value: string, validator: FilterType }
 * FE condition: { value, operator, output }
 *
 * Mapping:
 * - BE path → FE value (full reference, e.g. {parsedBody.key}, {data_surname})
 * - BE value → FE output
 * - BE validator → FE operator (FilterType values, no conversion needed)
 */
export function mapConnectionSettingsToEdgeData(connection: ConnectionData): Record<string, unknown> {
    // BE returns either [] (empty array) or { filter: [...] } (object with filter)
    // Need to check it's actually an object before accessing .filter property
    const settings = connection.settings;
    const isSettingsObject = settings && typeof settings === "object" && !Array.isArray(settings);
    const filters = isSettingsObject
        ? ((settings as Record<string, unknown>).filter as Array<{
              path: string;
              value?: string;
              validator: string;
              params?: { min?: string; max?: string };
          }>) || []
        : [];

    return {
        active: true,
        title: connection.title || "",
        subtitle: connection.subtitle || "",
        conditions: filters.map((filter) => {
            const isBetweenParams =
                BETWEEN_OPERATORS.includes(filter.validator as FilterType) &&
                typeof filter.params === "object" &&
                filter.params !== null;
            return {
                value: filter.path || "",
                operator: filter.validator || "equals",
                output: isBetweenParams ? filter.params?.min || "" : filter.value || "",
                output2: isBetweenParams ? filter.params?.max || "" : "",
            };
        }),
    };
}

// ============================================================================
// FE -> BE Mappers (formData to Step settings)
// ============================================================================

/**
 * Map FE formData to BE step settings based on node type
 * @param nodeType - FE node type (e.g., "mirrorNode")
 * @param formData - Form data from the node
 * @param nodeBeIdMap - Optional map of flow-position ID -> UUID for resolving references
 */
export function mapFormDataToStepSettings(
    nodeType: string,
    formData: Record<string, unknown>,
    nodeBeIdMap?: Map<string, string>
): Record<string, unknown> | undefined {
    switch (nodeType) {
        case "startNode":
            // Only integration start steps store meaning; others have no settings
            if (formData.beType === "integration") {
                return {
                    meaning: mapFlowToMeaning((formData.flow as string) || "post"),
                };
            }
            return {};

        case "httpNode": {
            const signatureData = mapFeSignatureToBe(formData.signature);
            if (import.meta.env.DEV) {
                console.log("[CC API] httpNode formData.signature:", formData.signature);
                console.log("[CC API] httpNode mapped signatureData:", signatureData);
            }
            return {
                method: (formData.method as string) || "GET",
                uri: (formData.endpoint as string) || "",
                headers: mapFeHeadersToBe(formData.headers),
                query: mapFeQueryToBe(formData.query),
                body: mapFeBodyToBe(formData.body),
                timeout: String((formData.timeout as number) || 30),
                format: (formData.format as string) || null,
                parser: (formData.parser as string) === "plain" ? null : (formData.parser as string) || null,
                notsend: (formData.notsend as string) || null,
                convert_data: (formData.convert_data as string) || null,
                ...(signatureData ? { signature: signatureData } : {}),
                ...((formData.insecure as boolean) ? { insecure: true } : {}),
            };
        }

        case "waitNode":
            return {
                delay_seconds: calculateDelaySeconds(formData),
            };

        case "setNode":
            return {
                values: mapFeSetValuesToBe(formData.fields),
            };

        case "modifyFieldNode":
            return {
                values: mapFeModifyValuesToBe(formData.data),
            };

        case "repeaterNode":
            return {
                repeats: (formData.maxRepetition as number) || 1,
            };

        case "breakerNode": {
            const reps = String(formData.maxRepetition ?? "").trim();
            const repeats = BREAKER_MAX_REPETITION_PATTERN.test(reps) ? reps : "1";
            return { repeats };
        }

        case "mirrorNode": {
            // mirrorElement is flow-position ID, need to resolve to UUID
            const mirrorElementId = (formData.mirrorElement as string) || "";
            const mirrorStepId = nodeBeIdMap?.get(mirrorElementId) || mirrorElementId || null;
            return {
                mirror_step_id: mirrorStepId,
            };
        }

        case "subScenarioNode":
            return {
                scenario_id: (formData.scenario as string) || null,
                start_step_id: null,
            };

        case "webhookNode":
            return {
                wait_for_webhook: (formData.wait as boolean) ?? false,
                timeout: String((formData.timeout as number) ?? 40),
                token: (formData.token as string) || "",
            };

        case "endNode":
            return {
                success: (formData.success as StepOutcome) !== StepOutcome.Failed,
            };

        case "conditionNode":
            return undefined;

        default:
            return undefined;
    }
}

/**
 * Map FE edge data to BE connection settings
 * Returns object with filter array, matching ConnectionSettingsRequest format
 *
 * FE condition: { value, operator, output }
 * BE FilterSettingsRequest: { path: string, value: string, validator: FilterType }
 *
 * Mapping:
 * - FE value → BE path (full reference, e.g. {parsedBody.key}, {data_surname})
 * - FE output → BE value
 * - FE operator → BE validator (FilterType values, no conversion needed)
 */
export function mapEdgeDataToConnectionSettings(
    edgeData: Record<string, unknown> | undefined
):
    | { filter?: Array<{ path: string; value?: string; validator: string; params?: { min: string; max: string } }> }
    | undefined {
    if (!edgeData) return undefined;

    const conditions = edgeData.conditions as Array<{
        value: string;
        operator: string;
        output: string;
        output2?: string;
    }>;

    if (!conditions || conditions.length === 0) return undefined;

    // Filter out empty/incomplete conditions (operator and value must both be set)
    const validConditions = conditions.filter((condition) => condition.operator?.trim() && condition.value?.trim());

    if (validConditions.length === 0) return undefined;

    return {
        filter: validConditions.map((condition) => {
            const path = condition.value.trim();
            const isBetween = BETWEEN_OPERATORS.includes(condition.operator as FilterType);
            return {
                path,
                validator: condition.operator,
                ...(isBetween
                    ? { params: { min: condition.output || "", max: condition.output2 || "" } }
                    : { value: condition.output || "" }),
            };
        }),
    };
}

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Convert BE headers object { key: value } to FE array [{ key, value }]
 */
function mapBeHeadersToFe(headers: unknown): Array<{ key: string; value: string }> {
    if (!headers || typeof headers !== "object" || Array.isArray(headers)) return [];
    return Object.entries(headers as Record<string, string>).map(([key, value]) => ({
        key,
        value: String(value ?? ""),
    }));
}

/**
 * Convert FE headers array [{ key, value }] to BE object { key: value }
 */
function mapFeHeadersToBe(headers: unknown): { [key: string]: string } | null {
    if (!headers || !Array.isArray(headers)) return null;
    const filtered = headers.filter((h: { key?: string }) => h.key);
    if (filtered.length === 0) return null;

    return filtered.reduce(
        (acc, h: { key: string; value: string }) => {
            acc[h.key] = h.value;
            return acc;
        },
        {} as { [key: string]: string }
    );
}

/**
 * Convert BE body (object or string) to FE array [{ key, value }]
 */
function mapBeBodyToFe(body: unknown): Array<{ key: string; value: string }> {
    if (!body) return [];

    // Handle string format
    if (typeof body === "string") {
        try {
            const parsed = JSON.parse(body);
            if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
                return Object.entries(parsed).map(([key, value]) => ({
                    key,
                    value: String(value),
                }));
            }
        } catch {
            // Not JSON, return as single field
            return [{ key: "body", value: body }];
        }
    }

    // Handle object format { key: value }
    if (typeof body === "object" && !Array.isArray(body)) {
        return Object.entries(body as Record<string, unknown>).map(([key, value]) => ({
            key,
            value: String(value ?? ""),
        }));
    }

    return [];
}

/**
 * Convert BE query object { key: value } to FE array [{ key, value }]
 */
function mapBeQueryToFe(query: unknown): Array<{ key: string; value: string }> {
    if (!query || typeof query !== "object" || Array.isArray(query)) return [];
    return Object.entries(query as Record<string, string>).map(([key, value]) => ({
        key,
        value: String(value ?? ""),
    }));
}

/**
 * Convert FE body array [{ key, value }] to BE object { key: value }
 */
function mapFeBodyToBe(body: unknown): { [key: string]: string } | null {
    if (!body || !Array.isArray(body)) return null;
    const filtered = body.filter((b: { key?: string }) => b.key);
    if (filtered.length === 0) return null;

    return filtered.reduce(
        (acc, b: { key: string; value: string }) => {
            acc[b.key] = b.value;
            return acc;
        },
        {} as { [key: string]: string }
    );
}

/**
 * Convert FE query array [{ key, value }] to BE object { key: value }
 */
function mapFeQueryToBe(query: unknown): { [key: string]: string } | null {
    if (!query || !Array.isArray(query)) return null;
    const filtered = query.filter((q: { key?: string }) => q.key);
    if (filtered.length === 0) return null;

    return filtered.reduce(
        (acc, q: { key: string; value: string }) => {
            acc[q.key] = q.value;
            return acc;
        },
        {} as { [key: string]: string }
    );
}

function mapBeSetValuesToFe(values: unknown): Array<{ key: string; value: string }> {
    if (!values || !Array.isArray(values)) return [];
    return values.map((v: { key?: string; value?: unknown }) => {
        const beKey = v.key || "";
        const beValue = String(v.value ?? "");
        // Key is stored without braces on BE; wrap so TagTextInput renders it as a tag.
        // Value is kept as-is — user types prefixes like parsedBody., query., headers. directly.
        return { key: beKey ? `{${beKey}}` : "", value: beValue };
    });
}

function mapFeSetValuesToBe(fields: unknown): Array<{ key: string; value: string }> {
    if (!fields || !Array.isArray(fields)) return [];
    return fields
        .filter((f: { key?: string }) => !!f.key)
        .map((f: { key: string; value: string }) => ({
            // Strip braces from key — BE expects plain name, not "{name}"
            key: f.key.replace(/^\{(.+)\}$/, "$1"),
            value: f.value,
        }));
}

/**
 * Map BE modify values to FE data structure
 *
 * BE modify settings structure (per field):
 * {
 *   path: string,  // field name/accessor
 *   filter: { source: string, type: string, params: string | { min: string, max: string } },
 *   change: { type: string, params: string, pattern?: string }
 * }
 *
 * FE data structure:
 * {
 *   fieldName: string,
 *   fields: [{ source, operator, value, value2, modification, pattern, output }]
 * }
 */
function mapBeModifyValuesToFe(values: unknown): Array<{
    fieldName: string;
    fields: Array<{
        source: string;
        operator: string;
        value: string;
        value2: string;
        modification: string;
        pattern: string;
        output: string;
    }>;
}> {
    if (!values || !Array.isArray(values)) return [];

    return values.map(
        (
            v:
                | Paldock.LaravelConnectionCreator.Http.Requests.Steps.ModifyValueRequest
                | { path?: string; value?: unknown; type?: string }
        ) => {
            // New structure with target/steps (ModifyValueRequest)
            if ("target" in v && "steps" in v) {
                const fields = (v.steps || []).map((step) => {
                    // Handle filter.params being either string or { min, max } object
                    let value = "";
                    let value2 = "";
                    const filterParams = step.filter?.params;
                    if (typeof filterParams === "object" && filterParams !== null) {
                        const paramsObj = filterParams as { min?: string; max?: string };
                        value = paramsObj.min || "";
                        value2 = paramsObj.max || "";
                    } else {
                        value = String(filterParams ?? "");
                    }

                    // Handle change.params being either string or { to, pattern } object
                    let output = "";
                    let pattern = "";
                    const changeParams = step.change?.params;
                    if (typeof changeParams === "object" && changeParams !== null) {
                        const paramsObj = changeParams as { to?: string; pattern?: string };
                        output = paramsObj.to || "";
                        pattern = paramsObj.pattern || "";
                    } else {
                        output = String(changeParams ?? "");
                    }

                    return {
                        source: step.source || "",
                        operator: step.filter?.type || "",
                        value,
                        value2,
                        modification: step.change?.type || "",
                        pattern,
                        output,
                    };
                });

                // target is stored without braces on BE; wrap so TagTextInput renders it as a tag.
                const rawTarget = v.target || "";
                return {
                    fieldName: rawTarget ? `{${rawTarget}}` : "",
                    fields:
                        fields.length > 0
                            ? fields
                            : [
                                  {
                                      source: "",
                                      operator: "",
                                      value: "",
                                      value2: "",
                                      modification: "",
                                      pattern: "",
                                      output: "",
                                  },
                              ],
                };
            }

            // Legacy flat structure with path/value/type - convert to new format
            const legacy = v as { path?: string; value?: unknown; type?: string };
            const legacyPath = legacy.path || "";
            return {
                fieldName: legacyPath ? `{${legacyPath}}` : "",
                fields: [
                    {
                        source: "",
                        operator: "",
                        value: String(legacy.value ?? ""),
                        value2: "",
                        modification: legacy.type || "replace",
                        pattern: "",
                        output: "",
                    },
                ],
            };
        }
    );
}

// Operators that use { min, max } params object instead of string
const BETWEEN_OPERATORS = [FilterType.IsBetween, FilterType.IsBetweenEquals];

/**
 * Map FE modify data to BE settings structure
 *
 * Uses BE types from app.d.ts:
 * - ModifyValueRequest: { source, target, steps }
 * - ModifyStepRequest: { index, filter, change }
 * - ModifyFilterRequest: { source, type, params }
 * - ModifyChangesRequest: { type, params }
 *
 * FE data structure:
 * {
 *   fieldName: string,
 *   fields: [{ source, operator, value, value2, modification, pattern, output }]
 * }
 */
function mapFeModifyValuesToBe(
    data: unknown
): Array<Paldock.LaravelConnectionCreator.Http.Requests.Steps.ModifyValueRequest> {
    if (!data || !Array.isArray(data)) return [];

    return data
        .filter(
            (entry: { fieldName?: string }) => entry.fieldName // Skip entries without fieldName
        )
        .map(
            (entry: {
                fieldName?: string;
                fields?: Array<{
                    source: string;
                    operator: string;
                    value: string;
                    value2: string;
                    modification: string;
                    pattern: string;
                    output: string;
                }>;
            }) => {
                const steps = (entry.fields || [])
                    .filter((field) => field.operator || field.modification) // Skip empty rows
                    .map((field, index) => {
                        // For between operators, params is { min, max } object
                        const isBetweenOperator = BETWEEN_OPERATORS.includes(field.operator as FilterType);
                        const filterParams = isBetweenOperator
                            ? { min: field.value || "", max: field.value2 || "" }
                            : field.value || "";

                        return {
                            index,
                            source: field.source || null,
                            filter: field.operator
                                ? {
                                      type: field.operator as Paldock.LaravelConnectionCreator.Enums.FilterType,
                                      params: filterParams,
                                  }
                                : null,
                            change: {
                                type: field.modification as Paldock.LaravelConnectionCreator.Enums.ModifyType,
                                params: field.pattern
                                    ? { to: field.output || "", pattern: field.pattern }
                                    : field.output || "",
                            },
                        };
                    });

                // Strip braces from fieldName — BE expects plain name, not "{name}"
                const rawFieldName = (entry.fieldName || "").replace(/^\{(.+)\}$/, "$1");
                return {
                    target: rawFieldName,
                    steps,
                };
            }
        );
}

function calculateDelaySeconds(formData: Record<string, unknown>): number {
    const value = (formData.delayValue as number) || 0;
    const unit = (formData.delayUnit as string) || "seconds";
    switch (unit) {
        case "minutes":
            return value * 60;
        case "hours":
            return value * 3600;
        case "days":
            return value * 86400;
        default:
            return value;
    }
}

// ============================================================================
// HTTP Signature Mappers
// ============================================================================

interface FeSignature {
    type: string; // Base type (e.g., "md5_values", "hmac_body")
    position?: string; // For MD5 types: "prefix" or "suffix"
    sha?: string; // For HMAC/DIGEST types: "256" or "512"
    delimiter: string; // "|" or "&"
}

interface BeSignature {
    algorithm: string; // Full HttpSignatureType (e.g., "md5_values_secret", "hmac_body_sha256")
    data_delimiter?: string;
}

/**
 * MD5 type mapping:
 * - prefix (secret first): md5_secret_values, md5_secret_sorted_values, md5_secret_keyval, md5_secret_sorted_keyval
 * - suffix (secret last): md5_values_secret, md5_sorted_values_secret, md5_keyval_secret, md5_sorted_keyval_secret
 */
const MD5_TYPE_MAP: Record<string, { baseType: string; position: "prefix" | "suffix" }> = {
    md5_secret_values: { baseType: "md5_values", position: "prefix" },
    md5_values_secret: { baseType: "md5_values", position: "suffix" },
    md5_secret_sorted_values: { baseType: "md5_sorted_values", position: "prefix" },
    md5_sorted_values_secret: { baseType: "md5_sorted_values", position: "suffix" },
    md5_secret_keyval: { baseType: "md5_keyval", position: "prefix" },
    md5_keyval_secret: { baseType: "md5_keyval", position: "suffix" },
    md5_secret_sorted_keyval: { baseType: "md5_sorted_keyval", position: "prefix" },
    md5_sorted_keyval_secret: { baseType: "md5_sorted_keyval", position: "suffix" },
};

/**
 * HMAC/DIGEST type mapping - extract base type and sha
 */
const SHA_TYPE_MAP: Record<string, { baseType: string; sha: "256" | "512" }> = {
    hmac_body_sha256: { baseType: "hmac_body", sha: "256" },
    hmac_body_sha512: { baseType: "hmac_body", sha: "512" },
    hmac_timestamp_body_sha256: { baseType: "hmac_timestamp_body", sha: "256" },
    hmac_timestamp_body_sha512: { baseType: "hmac_timestamp_body", sha: "512" },
    hmac_timestamp_json_sha256: { baseType: "hmac_timestamp_json", sha: "256" },
    hmac_timestamp_json_sha512: { baseType: "hmac_timestamp_json", sha: "512" },
    hmac_rfc7231_body_sha256: { baseType: "hmac_rfc7231_body", sha: "256" },
    hmac_rfc7231_body_sha512: { baseType: "hmac_rfc7231_body", sha: "512" },
    hmac_sorted_keyval_sha256: { baseType: "hmac_sorted_keyval", sha: "256" },
    hmac_sorted_keyval_sha512: { baseType: "hmac_sorted_keyval", sha: "512" },
    digest_base64_sha256: { baseType: "digest_base64", sha: "256" },
    digest_base64_sha512: { baseType: "digest_base64", sha: "512" },
    digest_keyid_hmac_sha256: { baseType: "digest_keyid_hmac", sha: "256" },
    digest_keyid_hmac_sha512: { baseType: "digest_keyid_hmac", sha: "512" },
    hmac_qsorted_keyval_nonce_bodyhash_sha256: { baseType: "hmac_qsorted_keyval_nonce_bodyhash", sha: "256" },
    hmac_qsorted_keyval_nonce_bodyhash_sha512: { baseType: "hmac_qsorted_keyval_nonce_bodyhash", sha: "512" },
};

/**
 * Map BE signature to FE signature structure (single signature)
 * BE: { algorithm: "md5_values_secret", data_delimiter: "|" }
 * FE: { type: "md5_values", position: "suffix", delimiter: "|" }
 *
 * BE: { algorithm: "hmac_body_sha256", data_delimiter: "|" }
 * FE: { type: "hmac_body", sha: "256", delimiter: "|" }
 */
function mapBeSignatureToFe(beSignature: unknown): FeSignature | null {
    if (!beSignature) return null;

    const sig = typeof beSignature === "object" ? beSignature : null;
    if (!sig) return null;

    const beSig = sig as BeSignature;
    if (!beSig.algorithm) return null;

    const delimiter = beSig.data_delimiter || "|";

    // Check if it's an MD5 type
    const md5Mapping = MD5_TYPE_MAP[beSig.algorithm];
    if (md5Mapping) {
        return {
            type: md5Mapping.baseType,
            position: md5Mapping.position,
            delimiter,
        };
    }

    // Check if it's an SHA type (HMAC/DIGEST)
    const shaMapping = SHA_TYPE_MAP[beSig.algorithm];
    if (shaMapping) {
        return {
            type: shaMapping.baseType,
            sha: shaMapping.sha,
            delimiter,
        };
    }

    return null;
}

/**
 * Map FE signature to BE signature structure (single signature)
 * FE: { type: "md5_values", position: "suffix", delimiter: "|" }
 * BE: { algorithm: "md5_values_secret", data_delimiter: "|" }
 *
 * FE: { type: "hmac_body", sha: "256", delimiter: "|" }
 * BE: { algorithm: "hmac_body_sha256", data_delimiter: "|" }
 */
function mapFeSignatureToBe(feSignature: unknown): BeSignature | null {
    if (!feSignature || typeof feSignature !== "object") return null;

    const sig = feSignature as FeSignature;
    if (!sig.type) return null;

    let algorithm: string;

    // MD5 types - combine base type with position
    if (sig.type.startsWith("md5_")) {
        if (sig.position === "prefix") {
            // secret first: md5_secret_X
            algorithm = sig.type.replace("md5_", "md5_secret_");
        } else {
            // suffix (default): md5_X_secret
            algorithm = sig.type + "_secret";
        }
    }
    // HMAC/DIGEST types - combine base type with sha
    else if (sig.type.startsWith("hmac_") || sig.type.startsWith("digest_")) {
        const sha = sig.sha || "256";
        algorithm = sig.type + "_sha" + sha;
    }
    // Unknown type
    else {
        return null;
    }

    return {
        algorithm,
        data_delimiter: sig.delimiter || "|",
    };
}
